import { definirLectureSeule } from './lectureSeule.js';
import { hydrate, type QueryClient } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';
import type { SessionUtilisateur } from '../api/types.js';
import { modeDemonstrationActif } from '../api/demo.js';
import {
  adopterIdentite,
  epoqueCourante,
  identiteCourante,
  surOubliIdentite,
  purgerCachesMetier,
  ouvrirEpoqueDeDroits,
  oublierIdentite,
} from '../auth/identite.js';
import { CLE_SESSION } from '../auth/queryClient.js';
import { compteConfine } from '../auth/confinement.js';
import { magasinMemoire } from './magasin.js';
import { magasinIndexedDb } from './magasinIndexedDb.js';
import { creerPersistance, type PersistanceHorsLigne } from './persistance.js';

let arreterSurveillance: (() => void) | null = null;
let expiration: ReturnType<typeof setTimeout> | null = null;
let instance: PersistanceHorsLigne | null = null;
let remplacee: PersistanceHorsLigne | null = null;

/** L'instance de l'application : IndexedDB si le navigateur l'offre. */
export function persistanceHorsLigne(): PersistanceHorsLigne {
  if (remplacee) return remplacee;
  instance ??= creerPersistance({
    magasin: typeof indexedDB === 'undefined' ? magasinMemoire() : magasinIndexedDb(),
    moteur: globalThis.crypto,
    maintenant: () => new Date(),
    build: __GIT_SHA__,
  });
  return instance;
}

/** Tests seulement. */
export function definirPersistanceHorsLigne(p: PersistanceHorsLigne | null): void {
  remplacee = p;
  terminerConsultation();
}

export interface EtatConsultation {
  readonly enregistreLe: string;
  readonly expireLe: string;
}

let consultation: EtatConsultation | null = null;
const abonnes = new Set<() => void>();
function annoncer(): void {
  for (const abonne of abonnes) abonne();
}

export function consultationCourante(): EtatConsultation | null {
  return consultation;
}

export function terminerConsultation(): void {
  arreterSurveillance?.();
  arreterSurveillance = null;
  consultation = null;
  definirLectureSeule(false);
  if (expiration) clearTimeout(expiration);
  expiration = null;
  annoncer();
}

export function useConsultationHorsLigne(): EtatConsultation | null {
  return useSyncExternalStore(
    (rappel) => {
      abonnes.add(rappel);
      return () => abonnes.delete(rappel);
    },
    consultationCourante,
    () => null,
  );
}

/**
 * Démarrage SANS réseau (M11) : l'exception délimitée à « aucun repli sur le
 * cache » (`auth/session.ts`). Seulement l'instantané de CETTE galerie (ou
 * l'unique instantané, depuis la racine), non expiré ; l'identité est adoptée
 * AVANT l'hydratation — adopter purge le cache métier.
 */
export async function restaurerSansReseau(
  client: QueryClient,
  slug: string | null,
): Promise<SessionUtilisateur | null> {
  if (modeDemonstrationActif()) return null;
  const avantLecture = epoqueCourante();
  const instantane = await persistanceHorsLigne().lire(slug);
  if (!instantane || avantLecture !== epoqueCourante()) return null;
  // La dernière session vue de ce compte était confinée (CGU à accepter,
  // double authentification à activer). Aucun repli ne lui ouvre la régie, et l'instantané qui
  // aurait survécu à l'effacement s'efface maintenant.
  if (compteConfine(instantane.compteId)) {
    await persistanceHorsLigne().purgerTout();
    return null;
  }
  await adopterIdentite(client, instantane.compteId, instantane.galerieSlug);
  const identite = identiteCourante();
  if (identite?.compteId !== instantane.compteId || identite.galerieSlug !== instantane.galerieSlug)
    return null;
  ouvrirEpoqueDeDroits();
  const avantPurge = epoqueCourante();
  await purgerCachesMetier(client);
  if (!(await persistanceHorsLigne().estCourant(instantane)) || avantPurge !== epoqueCourante())
    return null;
  definirLectureSeule(true);
  hydrate(client, instantane.requetes);
  client.setQueryData(CLE_SESSION, instantane.session);
  arreterSurveillance?.();
  arreterSurveillance = persistanceHorsLigne().surveiller(instantane, () => {
    // La génération a déjà changé ailleurs. Oublier seulement cet onglet :
    // ne pas purger les données du nouveau propriétaire ni reboucler les notifications.
    terminerConsultation();
    client.clear();
    void oublierIdentite(client, { localeSeulement: true });
  });
  consultation = { enregistreLe: instantane.enregistreLe, expireLe: instantane.expireLe };
  if (expiration) clearTimeout(expiration);
  expiration = setTimeout(
    () => {
      void oublierIdentite(client);
    },
    Math.max(0, Date.parse(instantane.expireLe) - Date.now()),
  );
  annoncer();
  return instantane.session;
}

surOubliIdentite(async ({ localeSeulement }) => {
  terminerConsultation();
  if (!localeSeulement && !modeDemonstrationActif()) await persistanceHorsLigne().purgerTout();
});
